import React, { useEffect, useMemo, useState } from "react";
import { IExpressionEvaluatorContext } from "../../../ExpressionsModule/Models/IExpressionEvaluatorContext";
import { VarExpression } from "../../../ExpressionsModule/Models/VarExpression";
import { usePersistedSetting, useQueryParamState } from "../../../common/hooks";
import { getListItemKey, startsWithPath } from "./listItemDisplay";

type UseSelectableListItemsProps = {
  componentId: string;
  componentPath: string[];
  itemPathSegment: string;
  items: any[] | undefined;
  itemsPending: boolean;
  keyField: string;
  persistedStateKey: string;
  persistSelection?: boolean;
  itemQueryKey?: string;
  selectedPath?: string[] | null;
  itemExpression?: VarExpression;
  currentItem?: any;
  context: IExpressionEvaluatorContext;
  enabled?: boolean;
  onSelected?: (item: any, index: number) => Promise<void> | void;
  onError: (error: unknown) => void;
};

export function useSelectableListItems({
  componentId,
  componentPath,
  itemPathSegment,
  items,
  itemsPending,
  keyField,
  persistedStateKey,
  persistSelection,
  itemQueryKey,
  selectedPath,
  itemExpression,
  currentItem,
  context,
  enabled = true,
  onSelected,
  onError,
}: UseSelectableListItemsProps) {
  const [persistedSelectedKey, setPersistedSelectedKey] = usePersistedSetting<string | number | null>(`${persistedStateKey}.selectedKey`, null);
  const [localSelectedKey, setLocalSelectedKey] = useState<string | number | null>(null);
  const [querySelectedKey, setQuerySelectedKey] = useQueryParamState(itemQueryKey);
  const selectedKey = enabled ? querySelectedKey ?? (persistSelection === false ? localSelectedKey : persistedSelectedKey) : null;

  const setSelectedKey = React.useCallback(
    (nextKey: string | number | null) => {
      if (persistSelection === false) {
        setLocalSelectedKey(nextKey);
      } else {
        setPersistedSelectedKey(nextKey);
      }
      setQuerySelectedKey(nextKey);
    },
    [persistSelection, setPersistedSelectedKey, setQuerySelectedKey]
  );

  const selectItem = React.useCallback(
    async (item: any, index: number) => {
      if (!enabled) {
        return;
      }

      const nextKey = getListItemKey(item, keyField, index);
      setSelectedKey(nextKey);

      try {
        if (itemExpression) {
          await context.Evaluator.assign(itemExpression, context, item);
        }

        await onSelected?.(item, index);
      } catch (error) {
        onError(error);
      }
    },
    [context, enabled, itemExpression, keyField, onError, onSelected, setSelectedKey]
  );

  const clearSelection = React.useCallback(async () => {
    setSelectedKey(null);

    if (!itemExpression) {
      return;
    }

    try {
      await context.Evaluator.assign(itemExpression, context, null);
    } catch (error) {
      onError(error);
    }
  }, [context, itemExpression, onError, setSelectedKey]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const itemsPath = [...componentPath, itemPathSegment];
    if (!startsWithPath(selectedPath, itemsPath)) {
      return;
    }

    const itemIndex = Number.parseInt(selectedPath?.[itemsPath.length] ?? "", 10);
    if (!Number.isInteger(itemIndex) || itemIndex < 0) {
      return;
    }

    const item = items?.[itemIndex];
    if (item === undefined || item === null) {
      return;
    }

    const nextKey = getListItemKey(item, keyField, itemIndex);
    if (String(nextKey) === String(selectedKey)) {
      return;
    }

    selectItem(item, itemIndex);
  }, [componentPath, enabled, itemPathSegment, items, keyField, selectedKey, selectedPath, selectItem]);

  useEffect(() => {
    if (!enabled || itemExpression || itemsPending || !items || items.length === 0 || selectedKey === null) {
      return;
    }

    const hasSelectedItem = items.some((item, index) => String(getListItemKey(item, keyField, index)) === String(selectedKey));
    if (!hasSelectedItem) {
      setSelectedKey(null);
    }
  }, [enabled, itemExpression, items, itemsPending, keyField, selectedKey, setSelectedKey]);

  useEffect(() => {
    if (!enabled || !itemExpression || itemsPending) {
      return;
    }

    if (!items || items.length === 0) {
      setSelectedKey(null);
      return;
    }

    if (currentItem === undefined || currentItem === null) {
      if (querySelectedKey !== null) {
        return;
      }

      setSelectedKey(null);
      return;
    }

    const currentKey = currentItem && typeof currentItem === "object" ? currentItem[keyField] ?? currentItem : currentItem;
    const matchedIndex = items.findIndex((item, index) => String(getListItemKey(item, keyField, index)) === String(currentKey));
    const nextKey = matchedIndex >= 0 ? getListItemKey(items[matchedIndex], keyField, matchedIndex) : null;
    if (nextKey !== selectedKey) {
      setSelectedKey(nextKey);
    }
  }, [currentItem, enabled, itemExpression, items, itemsPending, keyField, querySelectedKey, selectedKey, setSelectedKey]);

  const selectedItem = useMemo(() => {
    if (!enabled || selectedKey === null) {
      return undefined;
    }

    return items?.find((item, index) => String(getListItemKey(item, keyField, index)) === String(selectedKey));
  }, [enabled, items, keyField, selectedKey]);

  useEffect(() => {
    if (!enabled || !itemExpression || querySelectedKey === null || selectedItem === undefined || selectedItem === null) {
      return;
    }

    try {
      context.Evaluator.assign(itemExpression, context, selectedItem);
    } catch (error) {
      onError(error);
    }
  }, [context, enabled, itemExpression, onError, querySelectedKey, selectedItem]);

  return {
    selectedKey,
    selectedItem,
    selectItem,
    clearSelection,
    getItemKey: (item: any, index: number) => getListItemKey(item, keyField, index),
    componentScopedSelectionKey: `${componentId}.${selectedKey ?? ""}`,
  };
}
